import { createPortal } from "react-dom"
import useDropdownPortal from "../common/hooks/useDropdownPortal"
import { useEffect, useMemo, useRef, useState } from "react"
import { ReactComponent as ChevronIcon } from "../assets/images/icons/ic-chevron.svg"
import { Each } from "../common/Each"
import useClickOutside from "../common/hooks/useClickOutside"
import styles from "./MultipleSelect.module.css"
import Checkbox from "./Checkbox"
import TextInput from "./TextInput"

/**
 * MultipleSelect - Un componente React per selezionare più opzioni da un elenco.
 * 
 * Props:
 * @param {Array} selected - Gli elementi inizialmente selezionati (array di oggetti con almeno un `id` e `name`).
 * @param {Array} options - L'elenco delle opzioni disponibili (array di oggetti con almeno un `id` e `name`).
 * @param {Array} customOptions - Opzioni personalizzate con callback specifici per la selezione.
 *                                Ogni elemento è un oggetto con i seguenti campi:
 *                                - `id` (string): Identificativo univoco dell'opzione personalizzata.
 *                                - `name` (string): Nome dell'opzione personalizzata.
 *                                - `selected` (boolean): Indica se l'opzione personalizzata è selezionata.
 *                                - `onSelect` (function): Callback chiamato quando l'opzione viene selezionata o deselezionata.
 *                                Questo parametro è utile per implementare comportamenti personalizzati, come la selezione rapida
 *                                di un gruppo di elementi basati su criteri specifici.
 * @param {string} placeholder - Testo da mostrare quando nessuna opzione è selezionata.
 * @param {Object} style - Stile inline per il contenitore principale.
 * @param {Function} onSelect - Callback chiamato quando cambia la selezione. Riceve l'elenco aggiornato degli elementi selezionati.
 * @param {string} theme - Tema del componente, può essere 'dark' o 'light'.
 * @param {string} allLabel - Testo da mostrare per l'opzione "Seleziona tutto". Default: 'Tutti'.
 * @param {boolean} hideArrow - Se true, nasconde l'icona della freccia. Default: false.
 * @param {boolean} disabled - Se true, disabilita il componente. Default: false.
 *
 * 
 * Comportamento:
 * - Mostra un'etichetta con il nome dell'opzione selezionata o il numero di opzioni selezionate.
 * - Supporta la selezione di tutte le opzioni tramite un'opzione "Tutti".
 * - Disabilita l'interazione se la prop `disabled` è true.
 */

const MultipleSelect = ({ portal = false, appearance, selectedFirst = false, selected, options, customOptions, placeholder, style, onSelect = () => { }, theme, allLabel = 'Tutti', hideArrow = false, disabled = false, loading = false, onSearch, searchPlaceholder }) => {

    const [value, setValue] = useState(selected)
    const [opts, setOpts] = useState(options)
    const [optionsOpen, setOptionsOpen] = useState(false)
    const [searchValue, setSearchValue] = useState("")
    const [top, setTop] = useState(null)
    const containerRef = useRef(null)
    const ref = useRef(null)
    const portalStyle = useDropdownPortal(portal, optionsOpen, containerRef, ref)
    const renderOptions = (menu) => portal
        ? (optionsOpen ? createPortal(menu, containerRef.current?.closest('.starting-finance') || document.body) : null)
        : menu

    const handleClickOutside = (event) => {
        if (portal && ref.current?.contains(event.target)) return
        setOptionsOpen(false)
    }

    useClickOutside(containerRef, handleClickOutside)

    useEffect(() => {

        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver(() => {
            if (containerRef.current && ref.current) {
                let containerRect = containerRef.current.getBoundingClientRect()
                if (window.innerHeight - (containerRect.y + containerRect.height) < 150) {
                    setTop(-ref.current.clientHeight - 2)
                }
                else {
                    setTop(containerRef.current.clientHeight)
                }
            }
        });
        resizeObserver.observe(containerRef.current);

        return () => resizeObserver.disconnect(); // clean up 
    }, []);

    useEffect(() => {
        const query = (searchValue || "").trim().toLowerCase()
        let nextOpts = options || []

        if (query.length > 0) {
            nextOpts = nextOpts.filter((item) =>
                (item?.name || "").toLowerCase().includes(query)
            )
        }

        setOpts(nextOpts)
    }, [options, searchValue])


    const displayedOptions = useMemo(() => {
        if (!selectedFirst) return opts;
        const selectedIds = new Set((value || []).map(item => item.id));
        return [
            ...(opts || []).filter(item => selectedIds.has(item.id)),
            ...(opts || []).filter(item => !selectedIds.has(item.id)),
        ];
    }, [opts, value, selectedFirst]);

    const filteredCustomOptions = useMemo(() => {
        const query = (searchValue || "").trim().toLowerCase()
        if (query.length === 0) return customOptions || []

        return (customOptions || []).filter((option) =>
            (option?.name || "").toLowerCase().includes(query)
        )
    }, [customOptions, searchValue])

    let allSelected = useMemo(() => {
        if (!opts || opts.length === 0 || !value) return false
        return opts.map(v => v.id).every(v => value.map(o => o.id).includes(v))
    }, [opts, value])

    const handleOnSelect = async (item) => {
        let newValue = value
        if (value.map(v => v.id).includes(item.id)) {
            newValue = value.filter(v => v.id !== item.id)
        }
        else {
            newValue = [...value, item]
        }
        setValue(newValue)
        onSelect(newValue)
    }

    useEffect(() => {
        setValue(selected)
    }, [selected])

    useEffect(() => {
        if (!containerRef?.current || !ref?.current) return;

        if (containerRef && ref && containerRef.current && ref.current) {
            setTimeout(() => {
                if (containerRef && ref && containerRef.current && ref.current) {
                    let containerRect = containerRef.current.getBoundingClientRect()
                    if (window.innerHeight - (containerRect.y + containerRect.height) < 150) {
                        setTop(-ref.current.clientHeight - 2)
                    }
                    else {
                        setTop(containerRef.current.clientHeight)
                    }
                }
            }, 200)
        }
    }, [containerRef.current, ref.current, options])

    const selectAll = () => {
        let newValue = []
        if (!allSelected) {
            newValue = opts
            setValue(newValue)
        }
        else {
            newValue = []
            setValue(newValue)
        }
        onSelect(newValue)
    }


    return (
        <div
            style={style}
            className={`${styles.container} ${appearance === "modern" ? styles.modern : ""} ${optionsOpen && !disabled ? styles.open : ''} ${theme === 'dark' ? styles.dark : ''} ${disabled ? styles.disabled : ''}`}
            ref={containerRef}
            onClick={() => {
                if ((options.length > 0 || onSearch ) && !disabled && !loading) {
                    setOptionsOpen(!optionsOpen)
                }
            }} >
            <div className={styles.selectedLabel}>
                {value && value.length === 1 &&
                    <>
                        {value[0].name}
                    </>
                }
                {value && value.length > 1 &&
                    <>{value.length === opts.length ? (allLabel ? allLabel : 'Tutti') : value.length + ' selezionati'}</>
                }
                {(!value || value.length === 0) &&
                    <>
                        {placeholder}
                    </>
                }
            </div>

            {renderOptions(<div className={`${styles.options} ${appearance === "modern" ? styles.modernOptions : ""} ${((opts && opts.length > 0) || onSearch) && optionsOpen ? styles.open : ''}`} ref={ref} style={portal ? portalStyle : { top: `${top + 4}px` }} onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
            }}>
                <>
                    {onSearch && typeof onSearch === 'function' &&
                        <div className={styles.searchContainer}>
                            <TextInput appearance={appearance} type="search" placeholder={searchPlaceholder || "Cerca..."} className={styles.searchInput} onKeyUp={(value) => {
                                setSearchValue(value ?? "")
                                onSearch?.(value)
                            }} />
                        </div>
                    }
                    <div className={`${styles.option} ${allSelected ? styles.selected : ''}`} onClick={() => {
                        selectAll()
                    }}>
                        <Checkbox size="1.25rem" value={allSelected} radius="4px" /> Tutti
                    </div>
                    <Each of={filteredCustomOptions} render={(option) => {
                        return (
                            <div className={`${styles.option} ${value?.id === option.id ? styles.selected : ''}`} onClick={() => {
                                option.onSelect(!option.selected)
                            }}>
                                <Checkbox size="1.25rem" value={option.selected} radius="4px" /> {option.name}
                            </div>
                        )
                    }} />
                    <Each of={displayedOptions} render={(item) => {
                        return (
                            <div className={`${styles.option} ${value?.id === item.id ? styles.selected : ''}`} onClick={() => {
                                handleOnSelect(item)
                            }}>
                                <Checkbox size="1.25rem" value={value ? value.map(v => v.id).includes(item.id) : false} radius="4px" /> {item.name}
                            </div>
                        )
                    }} />
                </>
            </div>)}

            {!hideArrow && !loading &&
                <ChevronIcon className={`${styles.chevron} ${optionsOpen ? styles.open : ''}`} />
            }
            {loading &&
                <div className="loader" style={{ height: "16px" }} />
            }
        </div>
    )

}

export default MultipleSelect